// ─────────────────────────────────────────────────────────────────────────────
// 🚨 ONE SECTION MODEL, TWO SURFACES, TWO INDEPENDENT STATES PER ITEM.
//
// Jesse: "every checklist item has two independent states: provided (borrower
// has supplied what's asked) and verified (engine or staff has confirmed it).
// Borrower-facing completion and the step-5 Continue gate use provided. Staff
// views show both. Step 6 ('confirm income and assets') reads verified."
//
// 🔑 WHY ONE MODULE. There were THREE section systems, not two:
// getReviewSectionsForBorrower on the admin profile, getIncomeItems driving the
// borrower's progress bar, and docSets driving the borrower's upload slots. The
// last two disagreed with each other on the same page — the bar was computed
// from a single-type list resolved off the comma-joined employment_type while
// the slots were rendered per type off the canonical array, so every
// multi-employment borrower saw a bar that described a different file from the
// one in front of them.
//
// 🔑 WHY HERE. frontend/src/constants already holds readycheckPaths.js and
// readycheckStep1Gate.js — shared, surface-independent rules that neither page
// owns. This is not a component, so components/ is wrong, and putting it under
// either page would make one surface the owner of the other's behaviour.
//
// ── 🚨 provided AND verified ARE INDEPENDENT, AND THAT IS THE WHOLE POINT ────
//
// They are not two points on one scale. A document can be provided and never
// verified (nobody has looked), and an item can be verified without ever being
// provided (housing history confirmed from the credit report, identity from
// Persona). Collapsing them is what the old model did — admin's completeCount
// counted `status !== 'pending'`, so an UNREVIEWED upload read as complete, and
// the borrower's income progress counted mere presence as done.
//
// ⚠️ SO THE BORROWER'S GATE READS provided AND NOTHING ELSE. They have done what
// was asked of them the moment they upload; making their Continue button wait on
// staff review would block them on work that is not theirs. Step 6 reads
// verified, which is the question underwriting is actually asking.
// ─────────────────────────────────────────────────────────────────────────────

// ── D3: EXACT MATCHING, PLUS AN EXPLICIT ALIAS TABLE ───────────────────────
//
// 🚨 ADMIN MATCHED BY SUBSTRING (`t.includes(known)`) AND THAT DID ALL THE WORK.
// Production holds paystubs / w2s / 1099s / pl_statement and ZERO rows of
// pay_stub / w2 / 1099 / profit_loss — admin's slot lists only ever matched
// because 'w2' is a substring of 'w2s'. The same looseness made its asset slot
// ('bank_statement') swallow bank_statement_income, the one type the asset rule
// deliberately excludes.
//
// ⚠️ EXACT MATCHING THEREFORE NEEDS THE LEGACY SPELLINGS NAMED. Counted on real
// borrowers: 1099_forms (2), bank_statements (1), mortgage_statements (1),
// assets (1), income (5), lox (5), lox_letter (12). An alias is a decision about
// one historic value, not a pattern that can quietly widen.
import REQ from './incomeDocRequirements.json';

export const DOC_TYPE_ALIASES = {
  '1099s': ['1099_forms'],
  bank_statement: ['bank_statements', 'assets'],
  bank_statement_income: ['income'],
  // 🚨 'vor', 'vor_report' AND 'vor_landlord_form' ARE PARTNER TYPES (Brief 2
  // Phase 3). DoorlyPro's document picker offers every
  // constants/submissionDocuments ACCEPTED_TYPES value, a partner attaching a
  // verification of rent picks "vor", and utils/submissionAdoption.js carries
  // the type across unchanged — so the renter's VOR item stayed "Upload" with
  // the partner's VOR sitting in their documents, and step 5 refused to
  // continue on "Still needed: Housing." Found by the partner-referred e2e.
  vor_vom: ['mortgage_statements', 'private_mortgage_vom', 'vor', 'vor_report', 'vor_landlord_form'],
  pl_statement: ['schedule_c'],
};

/** Does this document satisfy this slot? Exact, plus declared aliases. */
export function docMatches(doc, docType) {
  const t = String((doc && doc.document_type) || '').toLowerCase();
  if (!t || !docType) return false;
  const want = String(docType).toLowerCase();
  if (t === want) return true;
  return (DOC_TYPE_ALIASES[want] || []).includes(t);
}

// ── D9: WHOSE ACCEPTANCE IS TRUSTED ────────────────────────────────────────
//
// Jesse: "section-acceptance short-circuit honours accepted_by 'staff' and
// 'auto_persona'. 'auto_misc' counts only when the section has zero outstanding
// required items. 'unknown' and null are not trusted: recompute from items."
//
// 🚨 THE OLD GATE DEMANDED 'staff' AND REJECTED 20 OF 29 REAL ACCEPTED ROWS.
// Production carries auto_persona (5), auto_misc (9 + 1 co_misc), 'unknown' (5
// co_* rows) and null (1). Nine misc sections accepted by auto_misc did not
// short-circuit, so a borrower whose letters were auto-cleared still saw misc
// incomplete.
export const TRUSTED_ACCEPTORS = ['staff', 'auto_persona', 'auto_manual_ai'];   // + the clean step-4 ID check (2026-10-19)
export const CONDITIONAL_ACCEPTORS = ['auto_misc'];

// ── 🚨 WHO MAY ACCEPT *THIS* SECTION ────────────────────────────────────────
//
// 🚨 THE FLAT LIST ABOVE SAID 'auto_persona' IS TRUSTED, FULL STOP. So an
// auto_persona row on INCOME or ASSETS would have completed those sections on an
// identity vendor's say-so — and on the server side, released a credit pull.
// Latent rather than live (nothing writes that combination today), which is
// exactly why it survived unnoticed.
//
// 🔑 PERSONA ANSWERS ONE QUESTION: is the applicant who they say they are. It has
// no view on whether their pay stubs reconcile, so it cannot accept a section
// about their money. Jesse, 2026-10-14: "auto_persona is trusted for identity
// sections only."
//
// ⚠️ MIRRORS backend/constants/verificationGate.js ACCEPTORS_BY_SECTION, which is
// the AUTHORITY — this screen renders per-item detail, the server decides. The
// default is the strict set, so a section missing from the map, or a row with no
// section, gets staff only.
export const STAFF_ONLY_ACCEPTORS = ['staff'];
export const ACCEPTORS_BY_SECTION = {
  identity: ['staff', 'auto_persona', 'auto_manual_ai'],
  co_identity: ['staff', 'auto_persona', 'auto_manual_ai'],
  income: STAFF_ONLY_ACCEPTORS,
  co_income: STAFF_ONLY_ACCEPTORS,
  assets: STAFF_ONLY_ACCEPTORS,
  co_assets: STAFF_ONLY_ACCEPTORS,
};

/** The acceptors for a section. An unknown section gets the strict set. */
export function acceptorsFor(section) {
  const key = String(section == null ? '' : section).trim().toLowerCase();
  return ACCEPTORS_BY_SECTION[key] || STAFF_ONLY_ACCEPTORS;
}

/**
 * Is this section accepted in a way that completes it outright?
 *
 * ⚠️ outstanding_items IS NOT A STORED COLUMN — routes/review.js computes it per
 * section ('misc' gets the live credit_conditions count, everything else 0). So
 * the conditional branch reads a server-supplied figure and cannot be derived
 * from the row.
 */
export function sectionAcceptance(row) {
  if (!row) return { accepted: false, reason: 'no review row' };
  const status = String(row.status || '').toLowerCase();
  if (status !== 'accepted' && status !== 'approved') {
    return { accepted: false, reason: `status is ${status || 'unset'}` };
  }
  const by = row.accepted_by == null ? null : String(row.accepted_by).toLowerCase();
  const outstanding = Number(row.outstanding_items || 0);
  // 🚨 A PENDING LETTER HOLDS ANY ACCEPTANCE (Jesse, 2026-10-21 — LOX Tier 1: "Miscellaneous is never complete
  // while a letter is pending"). A staff acceptance used to complete misc outright while a letter requested after it
  // was still unanswered or unread. The server narrows outstanding_items for a staff-accepted section to letters
  // raised after the acceptance, so a non-zero count is always a letter the acceptance could not have seen.
  if (by && outstanding > 0) {
    return { accepted: false, reason: `${by} but ${outstanding} letter(s) pending` };
  }
  // ⚠️ PER SECTION, NOT THE FLAT UNION. Both call sites find the row by
  // `r.section === name`, so the row always carries its section.
  if (by && acceptorsFor(row.section).includes(by)) {
    return { accepted: true, reason: `accepted by ${by}` };
  }
  // 🚨 A TRUSTED ACCEPTOR ON THE WRONG SECTION IS NOT AN ACCEPTANCE, and it says
  // so rather than falling through to "unidentified acceptor" — the two are
  // different facts and a reviewer reading the reason needs to know which.
  if (by && TRUSTED_ACCEPTORS.includes(by)) {
    return { accepted: false,
      reason: `${by} is not an acceptor for ${row.section || 'an unnamed section'}` };
  }
  if (by && CONDITIONAL_ACCEPTORS.includes(by)) {
    // 🚨 auto_misc IS CONDITIONAL. It fires off the application form's
    // declarations; a letter raised afterwards by ReadyCheck or an underwriter
    // lands in credit_conditions and never reaches form_data, so an acceptance
    // from it can be stale by the time anybody looks.
    return outstanding === 0
      ? { accepted: true, reason: 'auto_misc with nothing outstanding' }
      : { accepted: false, reason: `auto_misc but ${outstanding} item(s) outstanding` };
  }
  // ⚠️ 'unknown' AND null ARE NOT TRUSTED, AND THE FALLBACK IS TO RECOMPUTE —
  // not to reject. Returning accepted:false here means the section is scored
  // from its items, which is the honest answer when nobody can say who accepted
  // it. All five co_* rows and one identity row are in this state.
  return { accepted: false, reason: by ? `acceptor ${by} is not trusted` : 'no acceptor recorded' };
}

// ── D5: THE ASSET DOCUMENT TYPES ───────────────────────────────────────────
//
// ⚠️ KEEP IN LOCKSTEP WITH backend/constants/assetDocuments.js — the frontend
// cannot import from backend/, so this list exists twice by necessity and
// backend/test/assetDocumentation.test.js fails if the two diverge.
//
// 🚨 bank_statement_income IS EXCLUDED ON PURPOSE. It is explicitly typed for
// income, and letting it satisfy an asset requirement would mean the borrower
// whose assets are least verified is the one it clears.
export const ASSET_DOC_TYPES = ['bank_statement', 'bank_statements', 'bank_statement_assets',
  'assets', 'asset_statement', 'retirement_statement', 'investment_statement'];

// ── D1 + D2: THE INCOME ITEMS, BY EMPLOYMENT TYPE ──────────────────────────
//
// 🔑 THE ARRAY, MEMBERSHIP-TESTED, UNIONED. employment_types is the canonical
// multi-select; employment_type is that array comma-joined. Switching on the
// joined string sent every multi-type borrower to a single-type default — 'w2,
// 1099' matched no case at all. Production holds w2+other, w2+self_employed,
// w2+1099, self_employed+other and retired+other.
//
// 🔑 THE ITEMS COME FROM THE ONE DEFINITION (Jesse, 2026-10-23, step-5 fix 2): ./incomeDocRequirements.json, a
// byte-for-byte copy of backend/constants/incomeDocRequirements.json (backend test/incomeDocRequirements.test.js holds
// the two equal and every income type × choice to AFM v2). Every row is required.
//   · W-2: the most recent paystub, plus the latest W-2 OR a second paystub with YTD OR a written VOE (AFM v2). No
//     income bank-statement row (assets asks for statements).
//   · Military: the LES alone. Retired: one row, any one of the AFM v2 documents.
//   · Self-employed / 1099: the borrower's chosen path; no choice yet → one row asking for it, no documents.
export const WAGE_ITEMS = REQ.types.w2;
export const RETIRED_ITEMS = REQ.types.retired;
export const MILITARY_ITEMS = REQ.types.military;
export const OTHER_ITEMS = REQ.types.other;

// 🔑 THE RULE FOLLOWS THE BORROWER'S CHOSEN PATH — exclusive, per the guide: "ANY ONE of the following paths — NEVER
// more than one simultaneously."
export const SELF_EMPLOYED_BANK_STATEMENT_ITEMS = REQ.selfEmployed.bank_stmt_personal;
export const SELF_EMPLOYED_BUSINESS_ITEMS = REQ.selfEmployed.bank_stmt_business;
export const SELF_EMPLOYED_PATH_1099_ITEMS = REQ.selfEmployed.path_1099;
export const SELF_EMPLOYED_PATH_CHOICE_ITEMS = REQ.selfEmployed.unset;
export const SELF_EMPLOYED_FULL_DOC_EXTRA = {
  full_doc_sole: REQ.selfEmployed.full_doc_sole,
  full_doc_scorp: REQ.selfEmployed.full_doc_scorp,
};

export function selfEmployedItems(selfEmployedPath) {
  const p = String(selfEmployedPath || '').trim().toLowerCase();
  if (p === 'path_1099') return SELF_EMPLOYED_PATH_1099_ITEMS.slice();
  if (SELF_EMPLOYED_FULL_DOC_EXTRA[p]) return SELF_EMPLOYED_FULL_DOC_EXTRA[p].slice();
  if (p === 'bank_stmt_business') return SELF_EMPLOYED_BUSINESS_ITEMS.slice();
  if (p === 'bank_stmt_personal') return SELF_EMPLOYED_BANK_STATEMENT_ITEMS.slice();
  // ⚠️ NO CHOICE YET → THE CHOICE, NOT DOCUMENTS (decision e). This used to fall through to the personal list, which
  // is why choosing "Personal Bank Statement" changed nothing on screen.
  return SELF_EMPLOYED_PATH_CHOICE_ITEMS.slice();
}

/** The employment types on file, normalised. Array first, joined string second. */
// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE BORROWER'S INCOME TYPE, ONE SOURCE — the frontend copy of backend/constants/incomeType.js (2026-10-22). Here,
// not in its own module, because backend tests load this file without imports; backend/test/incomeTypeSource.test.js
// fails if the two ever map a spelling differently.
// ─────────────────────────────────────────────────────────────────────────────
export const INCOME_TYPES = Object.freeze(['w2', 'military', 'self_employed', '1099', 'rental', 'retired', 'other']);
export const INCOME_TYPE_LABELS = Object.freeze({ w2: 'W-2 employee', military: 'Military', self_employed: 'Self-employed',
  1099: '1099 / contractor', rental: 'Rental property income', retired: 'Retired', other: 'Other income' });

export function canonicalType(raw) {
  const s = String(raw == null ? '' : raw).trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!s) return null;
  if (/^(w_?2|w2_employee|w_2_employee|employed|salaried|hourly|wage|wages)$/.test(s)) return 'w2';
  if (/^(military|active_military|military_service)$/.test(s)) return 'military';
  if (/^(self_?employed|self_employed_business_owner|business_owner)$/.test(s)) return 'self_employed';
  if (/^(1099|1099_contractor|contractor|independent_contractor|gig)$/.test(s)) return '1099';
  if (/^(rental|rental_income|rental_property|rental_property_income)$/.test(s)) return 'rental';
  if (/^(retired|retirement|pension|social_security)$/.test(s)) return 'retired';
  if (/^(other|other_income)$/.test(s)) return 'other';
  return null;
}

const listOf = (v) => (Array.isArray(v) ? v : (v == null || v === '' ? [] : String(v).split(',')));

export function incomeTypesOf(formData = {}, user = {}) {
  for (const src of [formData && formData.employment_types, formData && formData.employment_type, user && user.lead_employment_type]) {
    const out = [];
    for (const r of listOf(src)) { const t = canonicalType(r); if (t && !out.includes(t)) out.push(t); }
    if (out.length) return out;
  }
  return [];
}

// 🔑 One source (incomeTypesOf above, 2026-10-22): canonical types, so 'W-2 Employee' / 'W2' / 'Self-Employed' ask for the
// right documents. An unrecognised spelling is kept as-is (lowercased) so nothing a borrower declared silently vanishes.
export function employmentTypes(formData) {
  const canon = incomeTypesOf(formData || {});
  if (canon.length) return canon;
  const raw = (formData && formData.employment_types)
    || (formData && formData.employment_type) || '';
  const list = Array.isArray(raw)
    ? raw
    : String(raw).split(',');
  return list
    .map((s) => String(s).trim().toLowerCase().replace(/[\s-]+/g, '_'))
    .filter(Boolean);
}

/**
 * Income items for every employment type on file, unioned and de-duplicated.
 *
 * ⚠️ DE-DUPLICATED BY docType. A w2 + 1099 borrower is asked for bank statements
 * by both lists, and showing the slot twice makes one upload look like two
 * outstanding asks.
 */
export function incomeItems(formData, selfEmployedPath) {
  const types = employmentTypes(formData);
  const out = [];
  const seen = new Set();
  const add = (items) => {
    for (const it of items) {
      if (seen.has(it.docType)) continue;
      seen.add(it.docType);
      out.push(it);
    }
  };
  // 🔑 AFM v2 (2026-10-23): military is the LES alone, not the wage list as well.
  if (types.includes('w2') || types.includes('w_2')) add(WAGE_ITEMS);
  if (types.includes('military')) add(MILITARY_ITEMS);
  if (types.includes('self_employed') || types.includes('1099')) add(selfEmployedItems(selfEmployedPath));
  if (types.includes('retired')) add(RETIRED_ITEMS);
  if (types.includes('other')) add(OTHER_ITEMS);
  // ⚠️ AN UNKNOWN OR EMPTY TYPE GETS THE WAGE LIST, NOT A SIX-ITEM CATCH-ALL.
  // Nine of 33 real applications store an empty employment_types, which was the
  // single largest population landing in admin's show-everything fallback — six
  // slots including tax returns and a P&L, for a borrower nobody had classified.
  // 🔑 A rental-only borrower has no fixed income documents: each rental brings its own rows (2026-10-26).
  if (out.length === 0 && !types.includes('rental')) add(WAGE_ITEMS);
  return out;
}

// ── D6, D7, D8: THE OTHER SECTIONS' ITEMS ──────────────────────────────────

// Jesse: "housing 'own' = provided with no document required at step 5; verified
// only after the credit pull confirms mortgage history (staff see 'awaiting
// credit'). Apply admin's accepting set for rent_free; blank status shows the
// placeholder item, not 0%."
//
// 🚨 THE TWO SYSTEMS DISAGREED ON EVERY HOUSING BRANCH. Admin made 'own'
// verifiedFromCredit (awaiting until the pull); the borrower's page returned 100
// unconditionally. 'rent_free' accepted vor / vor_vom / rent_free_lox on one
// surface and only an exact rent_free_lox on the other. A blank status — ten real
// borrowers — showed a placeholder item on one and a flat 0% on the other.
// 🔑 AND admin's 'mortgage' AND 'renting' BRANCHES ARE DEAD: production holds
// own (11), blank (10), rent (7) and rent_free (5), and nothing else.
/**
 * 🔑 THE OWNER'S HOUSING ROW, AS THE BORROWER SEES IT (Jesse, 2026-10-22). Before the pull: neutral, pending, nothing to
 * upload. After it, the real result — verified from the report (or by the borrower's "owned free and clear", or the
 * lender's VOM), or the step-7 mortgage question when the report shows no mortgage. Pure: a section-model item view.
 * → { state: 'pending'|'question'|'verified', tick, text }
 */
export function ownerHousingStatus(view, { creditPulled = false, ownerMortgage = null } = {}) {
  const om = ownerMortgage && ownerMortgage.state;
  if (view && view.verified) {
    const text = view.verifiedBy === 'owned_free_and_clear' ? 'You told us your home is owned free and clear.'
      : view.verifiedBy === 'lender_vom' ? 'Confirmed by your mortgage lender.'
        : 'Verified from your credit report.';
    return { state: 'verified', tick: true, text };
  }
  if (!creditPulled) return { state: 'pending', tick: false, text: 'We\u2019ll verify this from your credit report \u2014 nothing to upload.' };
  // 🔑 (2026-10-27) a file from before the steps 1–3 current-home questions: they are asked once, on step 7.
  if (om === 'application_questions') return { state: 'question', tick: false, text: 'We\u2019ll ask a few questions about your current home on step 7.' };
  if (om === 'awaiting_vom') return { state: 'pending', tick: false, text: 'We\u2019ve asked your mortgage lender to confirm your mortgage history.' };
  // 🔑 (2026-10-26) the application answered: a private lender's VOM is sent from step 5; a bank mortgage the report does
  // not show is cross-checked on step 7 — never "answer the question on step 7".
  if (om === 'vom_to_request') return { state: 'pending', tick: false, text: 'Ask your lender to verify, to send the request.' };
  if (om === 'crosscheck') return { state: 'pending', tick: false, text: 'Your credit report doesn\u2019t show this mortgage yet. We\u2019ve asked you about it.' };
  // 🔑 (2026-10-27) the answer and the report disagree (backend utils/housingConflict): neutral, never "verified".
  if (om === 'conflict') return { state: 'pending', tick: false, text: 'We\u2019re confirming your mortgage history.' };
  return { state: 'pending', tick: false, text: 'We\u2019ll verify this from your credit report \u2014 nothing to upload.' };
}

// ── 🔑 RENTALS AND THE CURRENT HOME (Jesse, 2026-10-26 — backend utils/rentalProperties) ───────────────────────────
// Each property's rows from its kind, the application's current-home answers and what was read; a property is PROVIDED
// when every required row has its document (the rent proof, the sale contract and the contingency are optional: without
// them the rent is not used / the payment counts, and the borrower can continue). components/guided/RentalProperties.
const rmoney = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;
/**
 * The rows a property needs, from its kind, the application's answers and what was read. Each row says which part of
 * Housing it belongs to (section: 'home' — the property's own card; 'mortgage' — the current home's Mortgage history,
 * 2026-10-27) and its short name for "Still needed:".
 * @param opts.vom  the private lender's VOM (status, escrowed) — not escrowed adds the tax statement and insurance page
 */
export function rentalRows(p, plan, opts = {}) {
  const f = p.figures || {};
  const isHome = p.kind === 'current_home';
  const other = p.kind === 'owned_other';
  const has = (role) => (p.docs || []).some((d) => d.role === role);
  const readOf = (role) => (p.docs || []).filter((d) => d.role === role && d.read).slice(-1)[0];
  const rows = [];
  const renting = isHome ? !!(plan && plan.plan === 'keeping' && plan.rentOut) : !other;
  const selling = isHome && plan && plan.plan === 'selling';
  const privateLender = isHome && plan && plan.hasMortgage && plan.holder === 'private';
  // 🔑 (2026-10-27) the seller with a bank mortgage gets the statement too; on the current home it sits in Mortgage history.
  if (!p.free_and_clear && !(isHome && plan && plan.hasMortgage === false) && !privateLender && (!isHome || (plan && plan.hasMortgage && (plan.plan === 'keeping' || renting || selling)))) {
    const st = readOf('mortgage_statement');
    rows.push({ role: 'mortgage_statement', docType: 'mortgage_statement', label: 'Latest mortgage statement', done: has('mortgage_statement'), button: 'Upload statement',
      section: isHome ? 'mortgage' : 'home', short: 'the latest statement',
      read: st && st.x && st.x.total_monthly_payment ? `${rmoney(st.x.total_monthly_payment)} a month${st.x.escrow_includes_taxes && st.x.escrow_includes_insurance ? ', taxes and insurance included' : ', no escrow'}` : null,
      note: 'We read the full monthly payment from it, including taxes and insurance if they’re paid through the loan.' });
  }
  const freeClear = !!p.free_and_clear || !!(isHome && plan && plan.hasMortgage === false);
  // 🔑 A private note with no escrow (the lender's VOM says so, 2026-10-27): the tax statement and the insurance page.
  const privateNoEscrow = privateLender && opts.vom && opts.vom.status === 'completed' && opts.vom.escrowed === false;
  // 🔑 The statement route (2026-10-28): 12 months of payments show no escrow, so the same two rows (backend
  // utils/rentalProperties completedVom — escrowed false), worded for payments rather than the lender's answer.
  const statementNoEscrow = privateLender && opts.vom && opts.vom.statementRoute === true;
  const needTaxIns = !privateLender && (freeClear || (f.missing || []).some((m) => m === 'tax_bill' || m === 'insurance') || has('tax_bill') || has('insurance'));
  if (needTaxIns) {
    if (!freeClear) rows.push({ role: null, info: 'Your statement doesn’t include taxes or insurance, so we also need these two:', section: 'home' });
    rows.push({ role: 'tax_bill', docType: 'property_tax_bill', label: 'Property tax statement', done: has('tax_bill'), button: 'Upload tax statement', section: 'home', short: 'tax statement' });
    rows.push({ role: 'insurance', docType: 'insurance_declarations', label: 'Homeowners insurance declarations page', done: has('insurance'), button: 'Upload declarations page', section: 'home', short: 'insurance' });
  }
  // 🔑 "TENANT HASN'T SIGNED OR PAID" (Jesse, 2026-10-28 — the approved HousingTenant mockup): on the current home it sets
  // BOTH the lease and the deposit / first month's rent aside (setAside — not required, not drawn); the full payment
  // counts and the rent is not used (utils/rentalProperties figuresFor → reason 'tenant_not_paid'). Either upload, or
  // "Add them now", brings them back (attachDocument / updateRental clear tenant_not_paid).
  const setAside = !!(isHome && renting && p.tenant_not_paid);
  if (renting) {
    const lease = readOf('lease');
    // 🔑 E1 A (Jesse 2026-11-01 — AFM v2 "a lease … or Schedule E"): a rental already owned may show its rent with the tax
    // return's Schedule E instead of a lease (backend utils/rentalProperties scheduleERentOf). The current home being
    // converted still needs the lease. Wording for Hannah: .scratch/reports/morning-decisions.md (np-batch).
    const leaseOrSchedE = !isHome && has('schedule_e');
    rows.push({ role: 'lease', docType: 'lease', label: 'The signed lease', done: has('lease') || leaseOrSchedE, button: 'Upload lease', section: 'home', short: 'the signed lease', setAside,
      optional: leaseOrSchedE || undefined,
      read: lease && lease.x && lease.x.monthly_rent ? `${rmoney(lease.x.monthly_rent)} a month` : null,
      note: isHome ? 'Signed by you and your tenant, showing the monthly rent.' : 'Or, if your latest tax return shows this rental on Schedule E, you can upload the return below instead.' });
    if (!isHome) {
      rows.push({ role: 'schedule_e', docType: 'tax_return_personal', label: 'Or your tax return with Schedule E', done: has('schedule_e'), button: 'Upload tax return', optional: true, section: 'home',
        note: 'Your latest federal return, including the Schedule E page that lists this property. We use the rent it shows.' });
    }
    // 🔑 THE PROOF FOLLOWS WHICH APPLIES (2026-10-27): a converted former residence, the deposit and first month's rent; any
    // other rental, the deposit and 3 months' rent (or Schedule E). Optional — and it says what happens without it.
    rows.push(isHome
      ? { role: 'rent_proof', docType: 'rental_deposit_statements', label: 'Proof of the deposit and first month’s rent', done: has('rent_proof'), button: 'Upload statement', optional: true, section: 'home',
        note: 'A bank statement showing both going into your account.', tenantNotPaid: true, setAside,
        result: 'Until we have this, we count the full payment on this home and don’t use the rent.' }
      : { role: 'rent_proof', docType: 'rental_deposit_statements', label: 'Proof the rent is being paid', done: has('rent_proof') || has('schedule_e'), button: 'Upload statements', optional: true, section: 'home',
        note: 'Bank statements showing the deposit and the last 3 months’ rent going into your account. Or, if your tax returns show this rental (Schedule E), upload them under Tax returns instead.',
        result: 'Until we have this, we count the full payment on this property and don’t use the rent.' });
  }
  if (privateNoEscrow || statementNoEscrow) {
    rows.push({ role: 'tax_bill', docType: 'property_tax_bill', label: 'Latest property tax statement', done: has('tax_bill'), button: 'Upload statement', section: 'home', short: 'tax statement',
      note: statementNoEscrow ? 'Your payments to your lender don’t show taxes and insurance. We add them to your monthly payment from this.'
        : 'Your lender told us taxes and insurance aren’t paid through the loan. We add them to your monthly payment from this.' });
    rows.push({ role: 'insurance', docType: 'insurance_declarations', label: 'Homeowners insurance declarations page', done: has('insurance'), button: 'Upload', section: 'home', short: 'insurance',
      note: 'The page that shows your yearly premium.' });
  }
  if (selling) {
    rows.push({ role: 'sale_contract', docType: 'sale_contract', label: 'The signed sale contract', done: has('sale_contract'), button: 'Upload contract', optional: true, section: 'home',
      note: 'Signed by you and your buyer.' });
    rows.push({ role: 'contingency', docType: 'sale_contingency_evidence', label: 'Proof the buyer’s financing is cleared (or clear to close)', done: has('contingency'), button: 'Upload proof', optional: true, section: 'home',
      note: 'Without both, your current home’s payment counts while we work out your loan.' });
  }
  return rows;
}

export const rentalPropertyDone = (p, plan, opts = {}) => rentalRows(p, plan, opts).filter((r) => r.role && !r.optional && !r.setAside).every((r) => r.done);

/** "Tenant hasn't signed or paid" (Jesse, 2026-10-28): the button, the set-aside box and its way back. */
export const TENANT_NOT_PAID = Object.freeze({ button: 'Tenant hasn’t signed or paid', title: 'The tenant hasn’t signed or paid yet',
  text: 'We’ll count the full payment on this home and won’t use the rent. You can add the lease and proof of payment later.', addNow: 'Add them now' });
// 🚨 NO DAY-14 OPTION ON STEP 5 (Jesse, 2026-10-29): step 5 offers the VOM route only ("Ask your lender to verify" + 2
// months of payments); the 12 months are offered on step 8 (components/guided/Step8VomControls) from the first visit.
// 🔑 THE LENDER'S CONTACT DETAILS, THE SAME RULES AS THE BACKEND (utils/privateMortgageVom.requestVom — the messages too).
export const VOM_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function vomContactErrors({ name = '', email = '' } = {}) {
  const e = {};
  if (String(name || '').trim().length < 2) e.name = 'Enter your lender’s name.';
  if (!VOM_EMAIL_RE.test(String(email || '').trim())) e.email = 'Enter your lender’s email address.';
  return e;
}
/** Step 8's "Sent to <email> on Oct 28." (the step-8 VOM controls, Jesse 2026-10-29). */
export function vomSentShort(email, sentAt, timeZone) {
  const d = sentAt ? new Date(sentAt) : null;
  const on = d && !Number.isNaN(d.getTime()) ? ` on ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(timeZone ? { timeZone } : {}) })}` : '';
  return `Sent to ${email || 'your lender'}${on}.`;
}
/** "Sent to <name> (<email>) on Oct 28, 2026." */
export function vomSentLine(vom, timeZone) {
  if (!vom) return null;
  const d = vom.sentAt ? new Date(vom.sentAt) : null;
  const on = d && !Number.isNaN(d.getTime()) ? ` on ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', ...(timeZone ? { timeZone } : {}) })}` : '';
  return `Sent to ${vom.lenderName}${vom.lenderEmail ? ` (${vom.lenderEmail})` : ''}${on}.`;
}

/** "Completed Oct 28, 2026." — the lender's VOM is in (null without a date). */
export function vomCompletedLine(vom, timeZone) {
  const d = vom && vom.completedAt ? new Date(vom.completedAt) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return `Completed ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', ...(timeZone ? { timeZone } : {}) })}.`;
}

const VERIFIED_OWNER_STATES = ['on_credit', 'vom_received', 'free_and_clear', 'staff_cleared', 'not_applicable'];
const monthsLine = (months) => {
  const names = (months || []).map((m) => { const [y, mo] = String(m).split('-').map(Number); return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' }); });
  const years = [...new Set((months || []).map((m) => String(m).slice(0, 4)))];
  return names.length ? `${names.slice(0, -1).join(', ')}${names.length > 1 ? ' and ' : ''}${names.slice(-1)[0]} ${years.join('–')}` : '';
};
/**
 * 🔑 HOUSING, IN ONE ORDER (Jesse, 2026-10-27 — the approved Step 5 Housing mockups). For an owner: the conflict cards
 * (the current-home answer vs the credit report), "Your current home · <address>", any other property they own, and
 * "Mortgage history" — each with its own header state, rows and what is still needed. components/guided/RentalProperties
 * renders it; the section model's gate reads the same parts. Null when there is no current home on file.
 */
// 🔑 WHILE A STEP-5 ASK IS OPEN, NOTHING RELATED READS AS COMPLETE (np-step5, Hannah's mockup review 2026-11-01): the
// server's r.asks (backend utils/step5Asks screenState — its words, approved): the home's tax / insurance rows are not done
// and the home's badge reads "<n> item(s) needed"; a VOM ask turns the VOM row's line into its "Received, but…" line and
// Mortgage history is not Done. The parts' done flags follow, so the Housing percentage drops too.
function withAsks(parts, asks) {
  if (!parts || !asks) return parts;
  const open = (part, a) => {
    part.done = false;
    part.status = { text: a.badge, tone: 'todo' };
  };
  const h = asks.byProperty && parts.home && parts.home.property ? asks.byProperty[parts.home.property.id] : null;
  if (h) {
    parts.home.rows = (parts.home.rows || []).map((row) => (row.role && h.roles.includes(row.role) ? { ...row, done: false, askNeeded: asks.rowNeeded || true } : row));
    open(parts.home, h);
  }
  for (const o of parts.others || []) {
    const ho = asks.byProperty ? asks.byProperty[o.property.id] : null;
    if (!ho) continue;
    o.rows = (o.rows || []).map((row) => (row.role && ho.roles.includes(row.role) ? { ...row, done: false, askNeeded: asks.rowNeeded || true } : row));
    open(o, ho);
  }
  const v = asks.vom && parts.home && parts.home.property ? asks.vom[parts.home.property.id] : null;
  if (v && parts.mortgage) {
    // …and nothing in it says confirmed: the sub-card's "Your lender confirmed…" text and Mortgage history's "✓ Your lender
    // confirmed your mortgage." are not shown while the ask is open (the row's own line says what is missing).
    if (parts.mortgage.vom) parts.mortgage.vom = { ...parts.mortgage.vom, status: { text: v.label, tone: 'todo', line: true }, text: null, askNeeded: true };
    parts.mortgage.lines = (parts.mortgage.lines || []).filter((l) => l.tone !== 'ok');
    open(parts.mortgage, { badge: v.badge });
  }
  return parts;
}

export function housingParts(r, ctx = {}) {
  return withAsks(housingPartsOf(r, ctx), r && r.asks);
}

function housingPartsOf(r, { ownerMortgage = null, creditPulled = false } = {}) {
  const rr = r || {};
  const props = Array.isArray(rr.properties) ? rr.properties : [];
  const home = props.find((p) => p.kind === 'current_home');
  if (!home) return null;
  const plan = rr.plan || null;
  const vom = rr.vom || null;
  const conflicts = Array.isArray(rr.conflicts) ? rr.conflicts : [];
  const openC = conflicts.filter((c) => c.status === 'open');
  const noteC = conflicts.filter((c) => c.status === 'needs_staff');
  const hold = openC.length ? 'conflict' : noteC.length ? 'note' : null;
  const om = ownerMortgage && ownerMortgage.state;

  const all = rentalRows(home, plan, { vom });
  const homeAll = all.filter((x) => x.section !== 'mortgage');
  // 🔑 (2026-10-28) "Tenant hasn't signed or paid": the lease and the deposit rows are set aside — the card shows the
  // set-aside box (and "Add them now") instead, and counts as done for what it set aside.
  const tenantSetAside = homeAll.some((x) => x.setAside);
  const homeRows = homeAll.filter((x) => !x.setAside);
  const proofRow = homeAll.find((x) => x.tenantNotPaid);
  const reqHome = homeRows.filter((x) => x.role && !x.optional);
  const homeMissing = reqHome.filter((x) => !x.done).map((x) => x.short || x.label);
  const homePart = {
    id: `home_${home.id}`, kind: 'home', property: home, part: 'your current home', title: `Your current home · ${home.address || ''}`,
    // "Something else" waits on Mortgage history; the home's own rows are decided once the note is checked.
    rows: hold ? [] : homeRows, missing: hold ? [] : homeMissing, done: hold === 'note' || (!hold && !homeMissing.length),
    // 🔑 Jesse (2026-10-27): no status in this header while a conflict is open; the normal status once it's answered.
    status: hold === 'conflict' ? null : hold === 'note' ? { text: 'Waiting on your note', tone: 'wait' }
      : !reqHome.length ? { text: tenantSetAside ? '✓ Done' : '✓ Nothing to upload', tone: 'ok' } : !homeMissing.length ? { text: '✓ Done', tone: 'ok' }
        : { text: `${reqHome.length - homeMissing.length} of ${reqHome.length}`, tone: 'todo' },
    box: hold === 'conflict' ? 'What this home needs is decided once you answer the question above.'
      : hold === 'note' ? 'What this home needs is decided once your note is checked.' : null,
    // The tenant choice: the button while the deposit isn't in; once chosen, the set-aside box with "Add them now".
    tenant: hold ? null : tenantSetAside ? { setAside: true, ...TENANT_NOT_PAID } : proofRow && !proofRow.done ? { setAside: false, ...TENANT_NOT_PAID } : null,
  };

  const others = props.filter((p) => p.kind === 'owned_other').map((p) => {
    const rows = rentalRows(p, plan);
    const req = rows.filter((x) => x.role && !x.optional);
    const missing = req.filter((x) => !x.done).map((x) => x.short || x.label);
    return { id: `other_${p.id}`, kind: 'other', property: p, part: 'another property you own', title: `Another property you own · ${p.address || ''}`, rows, missing, done: !missing.length,
      line: 'Its monthly payment counts in your debts.', status: !missing.length ? { text: '✓ Done', tone: 'ok' } : { text: 'To do', tone: 'todo' } };
  });

  // ── Mortgage history ──
  const mortgage = { id: 'mortgage_history', kind: 'mortgage', part: 'mortgage history', title: 'Mortgage history', rows: [], lines: [], vomForm: false, missing: [], done: false, verified: false, status: null };
  const privateLender = !!(plan && plan.hasMortgage === true && plan.holder === 'private');
  if (hold === 'conflict') {
    Object.assign(mortgage, { mode: 'conflict', lines: [{ text: 'Answer the question above first. We haven’t asked your lender anything yet.' }], status: { text: 'To do', tone: 'todo' } });
  } else if (hold === 'note') {
    Object.assign(mortgage, { mode: 'note', lines: [{ text: 'Waiting on your note to be checked against your credit report. Nothing for you to do right now.' }],
      status: { text: '◷ Waiting', tone: 'wait' }, missing: ['waiting on your note'] });
  } else if (!plan || plan.hasMortgage === null || om === 'application_questions') {
    Object.assign(mortgage, { mode: 'questions', lines: [{ text: 'We’ll ask a few questions about your current home on step 7.' }], done: true });
  } else if (plan.hasMortgage === false) {
    const verified = !!creditPulled && VERIFIED_OWNER_STATES.includes(om);
    Object.assign(mortgage, { mode: 'free_clear', done: true, verified, status: { text: '✓ Nothing to upload', tone: 'ok' },
      lines: [{ text: verified ? 'You told us your home is owned free and clear.' : 'We’ll verify this from your credit report — nothing to upload.', tone: verified ? 'ok' : null }] });
  } else if (privateLender) {
    const pp = rr.privatePayments || { months: [], found: false, uploaded: 0, reading: false, lenderName: null };
    const lender = (vom && vom.lenderName) || pp.lenderName || null;
    // 🔑 THE ROW'S STATE (Jesse, 2026-10-29 — backend utils/rentalProperties privatePaymentsFor): the statements in and
    // read with 2 months of mortgage-like payments → "✓ Received", the borrower's part done. The payee + amount match
    // against the VOM is a staff-only check once the VOM is in — never this row. (Older payloads: no state.)
    const ppState = pp.found ? 'found' : pp.state || (pp.uploaded > 0 && pp.reading ? 'reading' : (pp.months || []).length === 1 ? 'more_months' : 'none');
    const paymentsIn = ppState === 'found' || ppState === 'received';
    const paymentsDone = paymentsIn || ppState === 'reading';
    const one = ppState === 'more_months';
    const oneMonths = (pp.months || []).length ? pp.months : (pp.mortgageMonths || []);
    // 🔑 THE STATEMENT ROUTE (Jesse, 2026-10-29): the 12 months of payments to the lender (the AFM's preferred evidence;
    // backend utils/rentalProperties STATEMENT_ROUTE_MONTHS), uploaded from step 8 and accepted — in place of the VOM and
    // the 2 months. The lender's link is closed. (Step 5 itself offers only the VOM route.)
    if (vom && vom.statementRoute) {
      const need = Number(pp.need) || 12;
      const doneS = !!pp.found;
      const label = `${need} months of payments to your lender`;
      mortgage.rows = [{ role: 'private_payments', docType: 'private_mortgage_payments', label, done: doneS, button: 'Upload', section: 'mortgage',
        note: `Bank statements or cancelled checks showing your last ${need} payments to ${lender || 'your lender'}.`,
        found: doneS ? `Found in your bank statements: payments to ${lender || 'your lender'} in ${monthsLine(pp.months)}. Nothing else needed.`
          : (pp.months || []).length ? `We found payments to ${lender || 'your lender'} in ${(pp.months || []).length} of the ${need} months. Add the rest.` : null }];
      mortgage.vomForm = true;
      mortgage.vom = null;
      mortgage.payments = { title: label, status: doneS ? { text: '✓ Done', tone: 'ok' } : { text: 'To do', tone: 'todo' } };
      mortgage.missing = doneS ? [] : [`${need} months of payments`];
      mortgage.done = doneS;
      mortgage.verified = doneS;
      mortgage.mode = 'private_statements';
      mortgage.status = doneS ? { text: '✓ Done', tone: 'ok' } : { text: 'To do', tone: 'todo' };
      return { conflicts, openConflicts: openC, hold, home: homePart, others, mortgage };
    }
    mortgage.rows = [{ role: 'private_payments', docType: 'private_mortgage_payments', label: '2 months of payments to your lender', done: paymentsIn, state: ppState,
      button: ppState === 'no_payments' ? 'Upload statements' : 'Upload', section: 'mortgage',
      note: `Cancelled checks or bank statements showing your last 2 payments to ${lender || 'your lender'}.`,
      found: pp.found ? `Found in your bank statements: payments to ${lender || 'your lender'} in ${monthsLine(pp.months)}. Nothing else needed.`
        : ppState === 'reading' ? PRIVATE_PAYMENTS_READING_LINE
          : ppState === 'no_payments' ? PRIVATE_PAYMENTS_NOT_FOUND_LINE
            : one && oneMonths.length ? `We found a payment to ${lender || 'your lender'} in ${monthsLine(oneMonths)}. Add one more month.` : null }];
    const sent = vom && vom.status === 'sent';
    const completed = vom && vom.status === 'completed';
    mortgage.vomForm = true;
    // 🔑 TWO SUB-CARDS, EACH WITH ITS OWN STATUS (Jesse, 2026-10-28 — the approved HousingNew / HousingSent mockups):
    // "Verification of Mortgage" (the popup sends it) and "2 months of payments to your lender"; the header combines them.
    // 🔑 COMPLETED (Jesse, 2026-10-28 — item 5): the lender's VOM is in and passes (utils/privateMortgageVom.onVomCompleted)
    // → "✓ Completed" and "Completed <date>." in the same sub-card; Mortgage history says the lender confirmed it.
    mortgage.vom = { title: 'Verification of Mortgage', state: completed ? 'completed' : sent ? 'sent' : 'none',
      status: completed ? { text: '✓ Completed', tone: 'ok' } : sent ? { text: '✓ Sent', tone: 'ok' } : { text: 'To do', tone: 'todo' },
      text: completed ? 'Your lender confirmed the loan and your payments.' : sent ? 'We’ve asked your lender to confirm your payments.' : 'We’ll ask your lender directly to confirm the loan and your last 12 months of payments.',
      sentLine: completed ? vomCompletedLine(vom) : sent ? vomSentLine(vom) : null, button: completed ? null : sent ? 'Edit and resend' : 'Ask your lender to verify' };
    mortgage.payments = { title: '2 months of payments to your lender',
      status: ppState === 'reading' ? { text: '◷ Checking', tone: 'wait' } : paymentsIn ? (pp.uploaded > 0 ? { text: '✓ Received', tone: 'ok' } : { text: '✓ Done', tone: 'ok' }) : { text: 'To do', tone: 'todo' } };
    // 🔑 STEP 5 WAITS ONLY ON WHAT THE BORROWER CAN DO (Jesse, 2026-10-27): the lender's contact details (the VOM sent)
    // and the 2 months of payments. The lender's reply is a decision hold (utils/atlas; staff see it on the Module) —
    // never a step-5 block, never "waiting".
    mortgage.missing = !vom ? ['your lender’s contact details'] : !paymentsDone ? ['2 months of payments'] : [];
    mortgage.done = !!vom && paymentsDone;
    mortgage.verified = !!completed;
    mortgage.mode = 'private';
    const of2 = (vom ? 1 : 0) + (paymentsDone ? 1 : 0);
    mortgage.status = mortgage.done ? { text: '✓ Done', tone: 'ok' } : { text: `To do · ${of2} of 2`, tone: 'todo' };
    if (completed) mortgage.lines = [{ text: '✓ Your lender confirmed your mortgage.', tone: 'ok' }];
  } else {
    const st = all.find((x) => x.section === 'mortgage');
    mortgage.rows = st ? [st] : [];
    const verified = !!creditPulled && om === 'on_credit';
    mortgage.lines = [!creditPulled ? { text: 'After your credit check we also confirm this mortgage from your credit report.' }
      : verified ? { text: '✓ Verified from your credit report.', tone: 'ok' }
        : om === 'crosscheck' ? { text: 'Your credit report doesn’t show this mortgage yet. We’ve asked you about it.' } : { text: 'We’ll confirm this mortgage from your credit report.' }];
    mortgage.missing = st && !st.done ? ['the latest statement'] : [];
    mortgage.done = !mortgage.missing.length;
    mortgage.verified = verified;
    mortgage.mode = 'bank';
    mortgage.status = mortgage.done ? { text: '✓ Done', tone: 'ok' } : { text: 'To do', tone: 'todo' };
  }
  return { conflicts, openConflicts: openC, hold, home: homePart, others, mortgage };
}

// 🔑 A private lender's payments row (2026-10-29): still being read, and nothing on the statements that looks like a
// mortgage payment — asked for again the way any unreadable document is (NO_BALANCE_LINE). Nothing is "flagged".
export const PRIVATE_PAYMENTS_READING_LINE = 'We\u2019re reading your statements.';
export const PRIVATE_PAYMENTS_NOT_FOUND_LINE = 'We couldn\u2019t find your mortgage payments on these statements. Please upload statements that show the payments to your lender.';

/** The step-5 checklist items for the rentals (income) and the current home (housing). */
export function rentalItems(rentals, section, ctx = {}) {
  const r = rentals || {};
  const props = Array.isArray(r.properties) ? r.properties : [];
  if (section === 'income') {
    const own = props.filter((p) => p.kind === 'owned_rental');
    const items = own.map((p) => ({ id: `rental_${p.id}`, label: `Rental · ${p.address || ''}`, docType: null, rentalProperty: p.id, rentalDone: rentalPropertyDone(p, r.plan) }));
    if (r.wantsRentalIncome && !own.length) items.push({ id: 'rental_add', label: 'Add a rental property', docType: null, rentalProperty: 'add', rentalDone: false,
      note: 'Add each rental you own with its address; we’ll ask for its documents.' });
    return items;
  }
  // 🔑 HOUSING (2026-10-27): the parts above are the items — the gate and "Still needed:" read them; the screen draws them
  // in RentalProperties, never as rows at the bottom.
  const parts = housingParts(r, ctx);
  if (!parts) return [];
  const out = [];
  if (parts.openConflicts.length) out.push({ id: 'housing_conflict', label: 'About your current home’s mortgage', docType: null, rentalProperty: 'conflict', rentalDone: false, part: 'your current home’s mortgage', missingShort: [], housingPart: true });
  out.push({ id: parts.home.id, label: parts.home.title, docType: null, rentalProperty: parts.home.property.id, rentalDone: parts.home.done, part: parts.home.part, missingShort: parts.home.missing, housingPart: true });
  for (const o of parts.others) out.push({ id: o.id, label: o.title, docType: null, rentalProperty: o.property.id, rentalDone: o.done, part: o.part, missingShort: o.missing, housingPart: true });
  out.push({ id: 'mortgage_history', label: 'Mortgage history', docType: null, rentalProperty: 'mortgage', rentalDone: parts.mortgage.done, part: parts.mortgage.part, missingShort: parts.mortgage.missing, housingPart: true,
    verifiedPre: parts.hold ? false : parts.mortgage.verified });
  return out;
}

/**
 * "Housing (…)" — the parts of Housing still needed, for step 5's "Still needed:" line.
 * 🔑 ONE LEVEL OF DETAIL, NEVER TWO (Jesse, 2026-10-28): "Housing (mortgage history)", "Housing (your current home,
 * mortgage history)" — the part, never what inside it is open ("mortgage history: the latest statement" is gone); each
 * card on the screen says that itself.
 */
export function housingStillNeeded(views) {
  const open = (views || []).filter((v) => !v.optional && !v.provided);
  if (!open.length) return null;
  if (open.some((v) => v.part === 'your current home’s mortgage')) return 'your current home’s mortgage';
  const parts = [];
  for (const v of open) {
    const part = v.part || String(v.label || '').toLowerCase();
    if (!parts.includes(part)) parts.push(part);
  }
  return parts.join(', ');
}

// 🔑 The renter's own statements showing the rent (backend utils/rentVerification RENT_STATEMENTS_TYPE).
export const RENT_STATEMENTS_TYPE = 'rent_payment_statements';
/** "Waiting on <landlord> since <date>" — a sent VOR request, pending until the landlord submits the form. */
export function rentWaitingLine(rq) {
  if (!rq) return null;
  const d = rq.sentAt ? new Date(rq.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  return `Waiting on ${rq.landlordName || 'your landlord'}${d ? ` since ${d}` : ''}`;
}
export function housingItems(formData, rentVerification = null) {
  const hs = String((formData && formData.housing_status) || '').trim().toLowerCase();
  if (hs === 'own' || hs === 'mortgage') {
    return [{
      id: 'mortgage_history',
      label: 'Mortgage History',
      docType: 'vor_vom',
      // 🚨 NOTHING TO UPLOAD, SO IT IS PROVIDED ON SIGHT. Owners' mortgage
      // history is read off the credit report at ReadyCheck step 3; asking for a
      // VOM here is a need the borrower can never clear.
      providedWithoutDocument: true,
      verifiedBy: 'credit',
      // Jesse, 2026-10-22: the copy before the pull. After it, ownerHousingStatus says what the report showed.
      note: 'We\u2019ll verify this from your credit report \u2014 nothing to upload.',
    }];
  }
  if (hs === 'rent_free') {
    // AFM v2 §8: living rent-free takes the property owner's signed letter, not a letter of explanation.
    return [{ id: 'rent_free_lox', label: 'Rent-free letter from the property owner',
      docType: 'rent_free_lox',
      // ⚠️ ADMIN'S ACCEPTING SET, per the decision: a VOR or a combined VOR/VOM
      // satisfies this too. The borrower's page accepted only an exact
      // rent_free_lox, so a borrower who uploaded a VOR saw 0%.
      altDocTypes: ['vor_vom'],
      note: 'A signed letter from the property owner confirming you live there rent-free.' }];
  }
  if (hs === 'rent' || hs === 'renting') {
    // 🔑 THE RENTER'S HISTORY, PER AFM v2 (Jesse, 2026-10-26 — backend utils/rentVerification): "Bank statements showing
    // the payments are preferred; otherwise a VOR sent by Doorly directly … A private VOR needs 2 months of cancelled checks
    // or bank statements." The one row offers both from the start — 12 months of bank statements showing the rent
    // (rent_payment_statements: complete once the check confirms the payments) or the landlord's VOR (a sent request is
    // pending, never ✓: complete only when the landlord submits the form). A VOR the borrower or a partner uploaded counts
    // for neither. The private landlord's 2 months are their own row, on the VOR path only.
    const items = [{ id: 'vor', label: 'Rent history (12 months)', part: 'rent history', docType: 'vor_vom', altDocTypes: [RENT_STATEMENTS_TYPE], landlordRequest: true,
      note: 'Bank statements showing 12 months of rent payments, or ask your landlord to verify.' }];
    const rq = rentVerification && rentVerification.request;
    const statementsConfirmed = !!(rentVerification && rentVerification.statements && rentVerification.statements.confirmed);
    if (rq && rq.landlordType === 'individual' && !statementsConfirmed) {
      items.push({ id: 'rent_cheques', label: '2 months of cancelled checks or bank statements', part: 'rent history', docType: 'cancelled_checks_rent',
        note: 'For a private landlord: your two most recent cancelled rent checks, or 2 months of bank statements showing the rent paid to your landlord.' });
    }
    return items;
  }
  // ⚠️ BLANK STATUS SHOWS THE PLACEHOLDER, NOT 0%. Ten real borrowers have no
  // housing_status. A 0% section with no item is a bar with nothing behind it;
  // the placeholder at least names what is missing.
  return [{ id: 'housing_pending', label: 'Housing History (VOR / VOM)', docType: 'vor_vom',
    placeholder: true,
    note: 'Tell us your housing situation on the application and we will ask for the right thing.' }];
}

// Jesse: D7 — the union of both input sets.
//
// 🚨 THE TWO SYSTEMS READ DIFFERENT FIELDS ENTIRELY. Admin read
// declarations.{bankruptcy, foreclosure, behind_on_mortgage}; the borrower's page
// read requires_{bankruptcy, foreclosure, employment_gap, mortgage_default}_lox.
// Neither was a superset: admin had no employment-gap or mortgage-default item
// and the borrower's page had no behind_on_mortgage. Production has
// behind_on_mortgage on one borrower and requires_mortgage_default_lox on one.
const LOX_SOURCES = [
  { id: 'lox_bankruptcy', label: 'Letter of Explanation — Bankruptcy', docType: 'bankruptcy_lox',
    flags: ['requires_bankruptcy_lox'], declarations: ['bankruptcy'] },
  { id: 'lox_foreclosure', label: 'Letter of Explanation — Foreclosure', docType: 'foreclosure_lox',
    flags: ['requires_foreclosure_lox'], declarations: ['foreclosure'] },
  { id: 'lox_employment_gap', label: 'Letter of Explanation — Employment Gap',
    docType: 'employment_gap_lox', flags: ['requires_employment_gap_lox'], declarations: [] },
  { id: 'lox_mortgage_default', label: 'Letter of Explanation — Mortgage Late Payments',
    docType: 'mortgage_default_lox', flags: ['requires_mortgage_default_lox'],
    declarations: ['behind_on_mortgage'] },
];

const truthy = (v) => v === true || v === 1 || v === '1'
  || (typeof v === 'string' && ['true', 'yes', 'y'].includes(v.trim().toLowerCase()));

export function miscItems(formData) {
  const fd = formData || {};
  const decl = fd.declarations || {};
  const out = [];
  for (const src of LOX_SOURCES) {
    const byFlag = src.flags.some((f) => truthy(fd[f]));
    const byDecl = src.declarations.some((d) => truthy(decl[d]));
    if (byFlag || byDecl) {
      out.push({ id: src.id, label: src.label, docType: src.docType,
        // ⚠️ ALIASED TO THE GENERIC LOX TYPES, because production holds 12
        // lox_letter rows and 5 lox rows and ZERO of the four specific
        // spellings. Exact matching without this would show every letter
        // already written as still outstanding.
        altDocTypes: ['lox_letter', 'lox'],
        note: 'A short letter explaining what happened and when.',
        raisedBy: byFlag ? 'readycheck' : 'declaration' });
    }
  }
  return out;
}

// Jesse: D8 — admin's items; identity selectable in or out (step 5 excludes it).
export function identityItems() {
  return [
    { id: 'govt_id', label: 'Government-Issued Photo ID', docType: 'identity',
      altDocTypes: ['government_id', 'drivers_license', 'passport', 'photo_id', 'state_id'],
      // 🚨 PERSONA SATISFIES THIS. It captures and reads the ID, so a borrower
      // who verified through Persona has supplied it — and demanding a separate
      // upload is a need they cannot clear. The borrower's old page scored
      // identity as a flat 100 on kycStatus for exactly this reason; splitting
      // it into items without these flags would have dropped a Persona-verified
      // borrower to 50%.
      providedByPhotoId: true,
      verifiedBy: 'kyc',
      note: 'A driver\'s licence, state ID or passport — or verify with Persona instead.' },
    { id: 'kyc_verification', label: 'Identity Verification (Persona)', docType: null,
      verifiedBy: 'kyc',
      // 🔑 PROVIDED BY A PHOTO ID ANYWHERE ON THE FILE. Persona not returning
      // verified does not mean nothing was supplied — staff may hold a manual ID,
      // which is Craig's case and the reason admin grew a fallback.
      providedByPhotoId: true,
      note: 'Completed automatically when you verify with Persona.' },
  ];
}

// ── THE BUILDER ────────────────────────────────────────────────────────────

const SECTION_DEFS = [
  { id: 'identity', label: 'Identity', optional: true },
  { id: 'income', label: 'Income' },
  { id: 'assets', label: 'Assets' },
  { id: 'housing', label: 'Housing' },
  { id: 'misc', label: 'Letters of Explanation' },
];

/** 🚨 FIVE SECTIONS, ASSERTED. A sixth added without a decision is a section
 * nobody chose to gate on. */
export const EXPECTED_SECTIONS = 5;
if (SECTION_DEFS.length !== EXPECTED_SECTIONS) {
  throw new Error(`sectionModel: ${SECTION_DEFS.length} sections but EXPECTED_SECTIONS is `
    + `${EXPECTED_SECTIONS}. Change both in the same edit.`);
}

const itemDocTypes = (item) => [item.docType, ...(item.altDocTypes || [])].filter(Boolean);

/**
 * Build the full section model for ONE borrower.
 *
 * 🔑 ONE BORROWER PER CALL (D10). Admin calls it twice on a joint file with each
 * borrower's own documents; the borrower's own page calls it once. The two
 * surfaces partition documents by different mechanisms — admin by a synthesised
 * owner_type over two user_ids, the borrower by `WHERE user_id = ?` — and that
 * stays where it is rather than being reinvented here.
 *
 * @param {object} o
 *   formData           the application form object
 *   documents          this borrower's documents
 *   selfEmployedPath   users.self_employed_path
 *   kycStatus          'verified' | 'manual_review' | …
 *   creditPulled       whether the credit report is on file
 *   ownerMortgage      { state } from backend utils/ownerMortgage (guided screens); absent → the report verifies
 *   rentVerification   { request, landlordVorOnFile, chequesOnFile } from backend utils/rentVerification (renters)
 *   reviewSections     rows from review_sections (status, accepted_by, outstanding_items)
 *   assetSources       the borrower's asset sources (blob ∪ underwriting table)
 *   downPayment        form_data.down_payment
 *   plaid              { assets:boolean, income:boolean }
 *   giftSale           { giftRequests, netSheetRequests } from backend utils/giftSaleState (the gift and home-sale rows)
 *   includeIdentity    false for step 5, which excludes it
 */
export function buildSectionModel(o = {}) {
  const docs = Array.isArray(o.documents) ? o.documents : [];
  const reviews = Array.isArray(o.reviewSections) ? o.reviewSections : [];
  const reviewFor = (id) => reviews.find((r) => String(r.section) === id) || null;
  const kyc = String(o.kycStatus || '').toLowerCase();
  const kycDone = kyc === 'verified' || kyc === 'manual_review' || kyc === 'approved';
  const hasPhotoId = docs.some((d) => identityItems()[0] && itemDocTypes(identityItems()[0])
    .some((t) => docMatches(d, t)));

  const sections = [];
  for (const def of SECTION_DEFS) {
    if (def.id === 'identity' && o.includeIdentity === false) continue;

    let items;
    if (def.id === 'identity') items = identityItems();
    else if (def.id === 'income') items = [...incomeItems(o.formData, o.selfEmployedPath), ...rentalItems(o.rentals, 'income')];
    else if (def.id === 'housing') {
      // 🔑 (2026-10-27) an owner with the current home on file: the Housing parts (rentalItems/housingParts) are the items;
      // the old Mortgage History row is one of them. Without rentals (staff surfaces): as before.
      const parts = rentalItems(o.rentals, 'housing', { ownerMortgage: o.ownerMortgage, creditPulled: o.creditPulled });
      items = parts.length ? [...housingItems(o.formData, o.rentVerification).filter((i) => i.id !== 'mortgage_history'), ...parts]
        : housingItems(o.formData, o.rentVerification);
    }
    else if (def.id === 'misc') items = miscItems(o.formData);
    else items = assetItems(o);

    const acceptance = sectionAcceptance(reviewFor(def.id));

    const views = items.map((item) => {
      const matched = item.docType === null && !item.altDocTypes
        ? []
        : docs.filter((d) => itemDocTypes(item).some((t) => docMatches(d, t)));

      // ── provided ──────────────────────────────────────────────────────────
      // The borrower has supplied what was asked. An upload is enough; nobody
      // needs to have looked at it.
      // ── 🚨 A TRUSTED ACCEPTANCE MEANS IT WAS PROVIDED, TOO ────────────────
      //
      // 🚨 THE FIRST VERSION APPLIED ACCEPTANCE ONLY TO verified, AND IT DROPPED
      // TWO REAL BORROWERS TO ZERO. Forrest Jones (#40) has housing accepted by
      // staff and no VOR document; Corey Wesson (#48) has income accepted by
      // staff and a P&L rather than the 1099s the list now asks for. Both read
      // 100% before and 0% after — because "a human has signed this section off"
      // was being treated as saying nothing about whether the borrower supplied
      // anything. It says both: staff do not accept a section whose documents
      // are missing, and the borrower must not be asked again for something
      // already accepted.
      let provided = matched.length > 0 || acceptance.accepted;
      let providedBy = matched.length > 0 ? 'upload'
        : acceptance.accepted ? 'section_accepted' : null;
      // 🔑 THE LANDLORD'S VOR (2026-10-22): an upload is never the provider here — the sent request is the borrower's
      // part, the landlord's (or staff's) VOR the verification.
      // 🔑 (AFM v2, 2026-10-26) complete only when the check confirms the rent statements or the landlord submits the VOR;
      // a sent request, or statements not yet confirmed, is PENDING — not provided, no ✓, the section not 100%.
      let pendingRent = null;
      if (item.landlordRequest) {
        const rv = o.rentVerification || {};
        const live = !!(rv.request && rv.request.status === 'sent');
        // Statements (utils/rentHistoryReview): PROVIDED on upload — the check confirms them; a month it cannot match is
        // asked of the borrower (not provided until they show it). A sent VOR is pending until the landlord submits.
        const st = rv.statements || { enabled: false, onFile: 0, confirmed: false, unresolved: [] };
        const statementsIn = !!st.enabled && st.onFile > 0;
        const monthsOpen = statementsIn && st.status !== 'reading' && (st.unresolved || []).length > 0;
        provided = !!rv.landlordVorOnFile || (statementsIn && !monthsOpen) || acceptance.accepted;
        providedBy = rv.landlordVorOnFile ? 'landlord' : statementsIn && !monthsOpen ? 'rent_statements' : acceptance.accepted ? 'section_accepted' : null;
        if (!provided) pendingRent = monthsOpen ? 'months' : live ? 'landlord' : null;
        else if (statementsIn && !st.confirmed && !rv.landlordVorOnFile) pendingRent = st.status === 'reading' ? 'checking' : null;
      }
      // 🔑 "LATEST W-2, OR A SECOND PAYSTUB" (AFM v2, 2026-10-23): a second upload of `secondOf` satisfies the row.
      if (!provided && item.secondOf && docs.filter((d) => docMatches(d, item.secondOf)).length >= 2) {
        provided = true; providedBy = 'second_upload';
      }
      // 🔑 A rental / the current home: provided when its required rows have their documents (rentalPropertyDone).
      if (item.rentalProperty) { provided = !!item.rentalDone || acceptance.accepted; providedBy = item.rentalDone ? 'property_documents' : acceptance.accepted ? 'section_accepted' : null; }
      if (!provided && item.providedWithoutDocument) {
        provided = true; providedBy = 'no_document_required';
      }
      // 🔑 IDENTITY FROM THE IDENTITY RECORD, NEVER FROM DOCUMENTS ON FILE (Jesse, 2026-10-20/22). A staff surface passes
      // o.identityState (utils/identityAdminLabel identityStaffState): photos uploaded but never submitted are not
      // "provided"; a submission is provided; only a verification verifies. Without it (the borrower's own pages): as before.
      const idState = def.id === 'identity' && o.identityState ? o.identityState : null;
      if (idState) {
        provided = ['checking', 'verified', 'flagged', 'on_hold'].includes(idState.state);
        providedBy = provided ? `identity_${idState.state}` : null;
      } else if (!provided && item.providedByPhotoId && (kycDone || hasPhotoId)) {
        provided = true; providedBy = kycDone ? 'kyc' : 'manual_id';
      }
      if (!provided && o.plaid && o.plaid.assets && def.id === 'assets') {
        provided = true; providedBy = 'plaid';
      }
      if (!provided && o.plaid && o.plaid.income && def.id === 'income') {
        provided = true; providedBy = 'plaid';
      }

      // ── verified ──────────────────────────────────────────────────────────
      // 🚨 AN UPLOADED, UNREVIEWED DOCUMENT IS NOT VERIFIED (D4). admin's
      // completeCount counted `status !== 'pending'`, so an unreviewed upload
      // read as complete — this is the line that said so.
      // ⚠️ ADDITIVE, NOT FIRST-BRANCH-WINS. The first version tested
      // item.verifiedBy before looking at the documents, so a photo ID a staff
      // member had reviewed counted for nothing on an item whose other route to
      // verification was Persona. An item can be confirmed more than one way and
      // any of them is enough.
      let verified = false;
      let verifiedBy = null;
      let awaiting = null;
      if (item.landlordRequest) {
        const rv = o.rentVerification || {};
        if (rv.landlordVorOnFile) { verified = true; verifiedBy = 'landlord'; }
        else if (rv.statements && rv.statements.confirmed) { verified = true; verifiedBy = 'rent_statements'; }
        else if (pendingRent === 'checking') awaiting = 'check';
        else if (pendingRent === 'months') awaiting = 'borrower';
        else if (rv.statements && (rv.statements.staffItems || []).length) awaiting = 'staff review of the rent months';
        else if (rv.request && rv.request.status === 'sent') awaiting = 'landlord';
      }
      if (!item.landlordRequest && matched.some((d) => String(d.admin_status || '').toLowerCase() === 'reviewed')) {
        verified = true; verifiedBy = 'staff';
      }
      if (idState) {
        verified = idState.state === 'verified';
        verifiedBy = verified ? (idState.method || 'identity_record') : null;
        if (!verified) awaiting = idState.state === 'photos_uploaded' ? 'borrower to submit the ID'
          : idState.state === 'not_started' ? 'borrower' : idState.state === 'needs_fix' ? 'borrower to fix the ID' : 'identity check';
      } else if (!verified && item.verifiedBy === 'kyc' && kycDone) {
        verified = true; verifiedBy = 'persona';
      }
      // 🔑 The Housing parts say themselves whether they are verified (housingParts — a conflict verifies nothing).
      if (item.verifiedPre !== undefined) { verified = !!item.verifiedPre; verifiedBy = verified ? 'housing_parts' : null; }
      if (!verified && item.verifiedBy === 'credit') {
        // D6: verified only once the credit pull confirms mortgage history.
        // 🔑 AND ONLY IF THE REPORT HAS THE MORTGAGE (Jesse, 2026-10-22 — backend utils/ownerMortgage). An owner with no
        // mortgage on it is asked whether there is one: until they answer, or while the lender-direct VOM is out, the
        // history is not verified; "owned free and clear" or the VOM verifies it. No ownerMortgage (the staff page):
        // as before.
        const om = o.ownerMortgage && o.ownerMortgage.state;
        if (!o.creditPulled) awaiting = 'credit';
        else if (om === 'application_questions') awaiting = 'borrower';
        else if (om === 'awaiting_vom') awaiting = 'lender verification of mortgage';
        // 🚨 NEVER "VERIFIED FROM YOUR CREDIT REPORT" WITH A VOM ON THE SAME LOAN (Jesse, 2026-10-27): a private lender's
        // history is verified by the VOM only, and an answer that conflicts with the report verifies nothing until answered.
        else if (om === 'vom_to_request') awaiting = 'borrower';
        else if (om === 'conflict') awaiting = 'borrower';
        else if (om === 'crosscheck') awaiting = 'borrower';
        else {
          verified = true;
          verifiedBy = om === 'free_and_clear' ? 'owned_free_and_clear' : om === 'vom_received' ? 'lender_vom' : 'credit_report';
        }
      }
      // A trusted section acceptance verifies every item in it.
      if (!verified && acceptance.accepted && !idState) { verified = true; verifiedBy = 'section_accepted'; }
      if (!verified && !awaiting && provided) awaiting = 'review';
      if (!idState && !verified && !awaiting && item.verifiedBy === 'kyc' && hasPhotoId) {
        awaiting = 'staff review of the manual ID';
      }

      // 🔑 Verified from the credit report and not yet verified: a NEUTRAL pending row (no tick, no 100%) that is still
      // provided — it never blocks Continue and is never "Still needed" (Jesse, 2026-10-22).
      const pendingCredit = item.verifiedBy === 'credit' && !verified;
      // 🔑 Manual photos that were never submitted are listed as such — never as completing anything.
      const identityPhotos = idState && item.id === 'govt_id' ? idState.manualPhotos : undefined;
      return { ...item, matched, uploadedCount: matched.length,
        provided, providedBy, verified, verifiedBy, awaiting, pendingCredit, identityPhotos, pendingRent };
    });

    // ── ⚠️ A PLACEHOLDER IS COUNTED, AND IT IS NOT PROVIDED ─────────────────
    //
    // 🚨 EXCLUDING IT MADE THE SECTION VACUOUSLY 100%. Ten real borrowers have no
    // housing_status, and dropping their placeholder from the denominator moved
    // seven of them from 0% to 100% on a section where nothing had been supplied
    // and nothing had been asked — inflating completion is the opposite of
    // making the change visible.
    //
    // ⚠️ SO IT COUNTS AS AN OUTSTANDING ITEM. The borrower sees a named item
    // telling them what is missing instead of a bare 0% bar, which is the half
    // of the decision this file can act on; the percentage still says nothing
    // has been provided, because nothing has.
    // ── ⚠️ OPTIONAL ITEMS DO NOT GATE; PLACEHOLDERS DO ──────────────────────
    //
    // 🔑 AN OPTIONAL ITEM IS A SLOT THE BORROWER MAY USE, NOT AN ASK. The 1099s
    // on a bank-statement path are the case: supplying them shortens the
    // statement window, and not having them must not hold the section open.
    //
    // 🚨 A PLACEHOLDER IS THE OPPOSITE AND STAYS COUNTED. Excluding it made the
    // housing section vacuously 100% and moved seven real borrowers from 0% to
    // 100% on a section where nothing had been supplied and nothing asked.
    const required = views.filter((v) => !v.optional);
    const pct = (list, key) => (list.length === 0 ? 100
      : Math.round((list.filter((v) => v[key]).length / list.length) * 100));
    // 🔑 LETTERS PENDING ON THE SERVER (misc only): a letter of explanation not yet accepted by staff. Misc is never
    // VERIFIED while one is pending — whatever its items say; provision (the step-5 gate) is unchanged.
    const pendingLetters = def.id === 'misc' ? Math.max(0, Number((reviewFor('misc') || {}).outstanding_items) || 0) : 0;
    const verifiedPctRaw = pct(required, 'verified');

    sections.push({
      id: def.id,
      label: def.label,
      items: views,
      acceptance,
      // ⚠️ A PLACEHOLDER IS NOT A REQUIREMENT. It names what is missing for a
      // borrower nobody has classified; counting it would hold them at 0% for a
      // question the application never asked them.
      providedPct: def.id === 'assets' ? assetsCompletion(o, views).pct : pct(required, 'provided'),
      // 🔑 "Still needed: Housing (…)" names the part of Housing (2026-10-27, the approved mockups).
      ...(def.id === 'housing' ? { stillNeeded: housingStillNeeded(views) } : {}),
      verifiedPct: pendingLetters > 0 ? Math.min(verifiedPctRaw, 99) : verifiedPctRaw,
      provided: def.id === 'assets'
        ? assetsCompletion(o, views).complete
        : required.every((v) => v.provided),
      // 🔑 The bar's own rule, carried for the status line (Jesse, 2026-10-26): what is left, from the same function.
      ...(def.id === 'assets' ? { completion: assetsCompletion(o, views) } : {}),
      // ⚠️ VACUOUSLY TRUE FOR AN EMPTY SECTION, matching `provided` and the 100
      // that verifiedPct already returns. `required.length > 0 &&` made misc
      // report verifiedPct 100 and verified false at the same time, which is a
      // contradiction any consumer would eventually trip over.
      verified: pendingLetters === 0 && required.every((v) => v.verified),
      outstanding: required.filter((v) => !v.provided).length,
      pendingLetters,
      // 🔑 A section held only by the credit report shows "Pending", never a percentage (Jesse, 2026-10-22).
      pendingCredit: required.some((v) => v.pendingCredit),
    });
  }

  // ── D12: AGGREGATION ───────────────────────────────────────────────────────
  // ⚠️ A SECTION WITH NO ITEMS IS NOT COUNTED, the way misc was always dropped
  // when empty. And assets does not count for a co-borrower — assets are a
  // property of the loan file, not of a person.
  const counted = sections.filter((s) => {
    if (s.id === 'misc' && s.items.length === 0) return false;
    if (s.id === 'assets' && o.isCoBorrower) return false;
    return true;
  });
  const mean = (key) => (counted.length === 0 ? 0
    : Math.round(counted.reduce((a, s) => a + s[key], 0) / counted.length));

  return {
    sections,
    counted: counted.map((s) => s.id),
    providedPct: mean('providedPct'),
    verifiedPct: mean('verifiedPct'),
    // The step-5 Continue gate. Reads provided, and nothing else.
    gate: {
      ready: counted.every((s) => s.provided),
      missing: counted.filter((s) => !s.provided).map((s) => s.label),
    },
    // Step 6 — "confirm income and assets".
    step6: {
      ready: ['income', 'assets'].every((id) => {
        const s = sections.find((x) => x.id === id);
        if (!s) return true;
        if (id === 'assets' && o.isCoBorrower) return true;
        return s.verified;
      }),
    },
  };
}

// ── D5: ASSETS ARE ITEMS AND A DOLLAR RULE AT ONCE ─────────────────────────
//
// 🔑 BOTH SYSTEMS WERE RIGHT ABOUT DIFFERENT THINGS. Admin had slots (a
// verification document, a gift letter) and no notion of amounts; the borrower's
// page had a dollar ratio over declared sources and no gift concept. Staff need
// the slots, the borrower needs the ratio, and one model carries both.
function assetItems(o) {
  const fd = o.formData || {};
  const out = [{ id: 'asset_verification', label: 'Account Statements',
    docType: 'bank_statement',
    altDocTypes: ['bank_statements', 'bank_statement_assets', 'asset_statement',
      'retirement_statement', 'investment_statement', 'assets'],
    note: 'One current statement per account you are using, showing the balance.' }];
  // 🔑 A GIFT DECLARED AS A SOURCE HAS ITS OWN ROW (Jesse, 2026-10-23 — step-5 fixes, item 3): "Add a gift" carries the
  // donor's letter, so the generic upload item is only for a file that has gift papers and no gift declared.
  const giftSourceDeclared = (Array.isArray(o.assetSources) ? o.assetSources : []).some(isGiftSource);
  const hasGift = !giftSourceDeclared && (truthy(fd.has_gift_funds)
    || String(fd.down_payment_source || '').toLowerCase() === 'gift'
    || (Array.isArray(o.documents) && o.documents.some((d) =>
      String(d.document_type || '').toLowerCase().includes('gift'))));
  if (hasGift) {
    // 🔑 FIX 6: the donor's own letter and statement (the donor page files them voa_gift_letter / voa_gift_statements)
    // satisfy this item too.
    out.push({ id: 'gift_letter', label: 'Gift Letter & Proof of Funds',
      docType: 'gift_letter', altDocTypes: ['gift_statements', 'voa_gift_letter', 'voa_gift_statements'],
      note: 'A signed gift letter from the donor, plus proof the funds exist.' });
  }
  return out;
}

// ── 🔑 GIFTS AND HOME-SALE PROCEEDS, ROW BY ROW (Jesse, 2026-10-23 — step-5 fixes, item 3) ──────────────────────────
//
// Each gift and each home sale is its own row on step 5's Assets section, saved through the classic page's routes and
// records ('Gift Funds' / 'Sale of Property', gift_letter_requests, net_sheet_requests). A row is PROVIDED when the
// paperwork is in — the signed letter (with the donor's statement when the gift is not yet deposited); the net sheet or
// final settlement statement. Asking the donor or the escrow agent is PENDING, not provided (Jesse, 2026-10-26). Verified is
// unchanged: staff apply the documented figure. Mirrors backend utils/giftSources (the per-gift rule ReadyCheck uses).
// Borrower wording: nothing here says anyone reviews anything.
export const GIFT_SOURCE_TYPE = 'Gift Funds';
export const SALE_SOURCE_TYPE = 'Sale of Property';
export const SALE_DOC_TYPES = { netSheet: 'sale_net_sheet', finalSettlement: 'sale_settlement_statement', proceeds: 'sale_proceeds_statements' };
// 🔑 STEP-5 FIX 2 (Jesse, 2026-10-25): the sale's money must be in the account before an offer (backend utils/offerGate).
export const SALE_PROCEEDS_LINE = 'The money from your sale needs to be in your account before you make an offer on a home. Once your sale closes, upload a statement showing the funds have arrived.';
export const isGiftSource = (s) => /^gift( funds)?$/i.test(String((s && s.source_type) || '').trim());
export const isSaleSource = (s) => /^(sale|sale of property)$/i.test(String((s && s.source_type) || '').trim());
const giftRequestIdOf = (s) => {
  const own = Number(s && s.gift_request_id);
  if (own > 0) return own;
  const m = String((s && s.id) || '').match(/^gift_(\d+)$/);
  return m ? Number(m[1]) : null;
};
const isYes = (v) => ['yes', 'true', '1', 'deposited'].includes(String(v == null ? '' : v).trim().toLowerCase());
const donorOf = (s) => String((s && (s.donor_name || String(s.source_name || '').replace(/^Gift from\s+/i, ''))) || '').trim();
// 🔑 STEP-5 FIX 1 (Jesse, 2026-10-25): a deposited gift is shown by the statement of the account it went into, with the
// deposit visible; it counts once the document check has read it (backend utils/giftSources depositStatementState).
export const GIFT_DEPOSIT_STATEMENT_TYPE = 'gift_deposit_statements';
const depositRow = (s, docs, okText) => {
  const ids = (Array.isArray(s && s.depositStatementDocumentIds) ? s.depositStatementDocumentIds : []).map(Number);
  const mine = docs.filter((d) => ids.includes(Number(d.id)) && String(d.document_type) === GIFT_DEPOSIT_STATEMENT_TYPE);
  if (!mine.length) return { provided: false, state: 'needs_deposit_statement', text: 'Upload the statement for the account the gift went into, with the deposit showing' };
  const read = mine.some((d) => String(d.ai_processing_status || '').toLowerCase() === 'complete' || String(d.admin_status || '').toLowerCase() === 'reviewed');
  return read ? { provided: true, state: 'letter_received', text: okText } : { provided: true, state: 'deposit_statement_reading', text: 'Statement received — we are reading it' };
};

/** { provided, state, text } for one gift row. */
export function giftRowStatus(s, { giftSale, documents } = {}) {
  const reqs = (giftSale && Array.isArray(giftSale.giftRequests)) ? giftSale.giftRequests : [];
  const docs = Array.isArray(documents) ? documents : [];
  const donor = donorOf(s) || 'your donor';
  const reqId = giftRequestIdOf(s);
  const req = reqId ? reqs.find((r) => Number(r.id) === reqId) : null;
  if (req && req.status === 'completed') {
    if (isYes(req.giftDeposited)) return depositRow(s, docs, `Signed gift letter received from ${donor}`);
    return { provided: true, state: 'letter_received', text: `Signed gift letter received from ${donor}` };
  }
  // 🚨 WAITING IS NOT COMPLETE (Jesse, 2026-10-26): Assets is done only when every declared source has its paperwork, so
  // a gift whose donor has not signed holds the section (it was `provided: true` — "the borrower has done their part" —
  // and Assets read 100% while the letter was unsigned). Backend utils/giftSources giftSupport already said unsupported.
  if (req) return { provided: false, pending: true, state: 'waiting_donor', text: `Waiting on ${donor} to sign the gift letter` };
  const letterId = Number(s && s.letterDocumentId);
  if (letterId > 0 && docs.some((d) => Number(d.id) === letterId)) {
    const stmtIds = (Array.isArray(s.statementDocumentIds) ? s.statementDocumentIds : []).map(Number);
    if (isYes(s.giftDeposited)) return depositRow(s, docs, 'Gift letter received');
    if (!docs.some((d) => stmtIds.includes(Number(d.id)))) {
      return { provided: false, state: 'needs_statement', text: 'Add the donor\u2019s bank statement showing the gift funds' };
    }
    return { provided: true, state: 'letter_received', text: 'Gift letter received' };
  }
  const name = donor.toLowerCase();
  if (name && reqs.some((r) => r.status === 'completed' && String(r.donorName || '').trim().toLowerCase() === name)) {
    return { provided: true, state: 'letter_received', text: `Signed gift letter received from ${donor}` };
  }
  if (docs.some((d) => String(d.document_type) === `asset_${s && s.id}`)) return { provided: true, state: 'letter_received', text: 'Gift letter received' };
  return { provided: false, state: 'needs_letter', text: 'Ask your donor to sign a gift letter, or upload one' };
}

/** { provided, state, text } for one home-sale row. */
export function saleRowStatus(s, { giftSale, documents } = {}) {
  const docs = Array.isArray(documents) ? documents : [];
  const has = (t) => docs.some((d) => String(d.document_type) === t);
  // 🔑 THE PROCEEDS STATEMENT IS WHAT LETS AN OFFER GO (fix 2); the final settlement statement supports the sale only.
  const proceeds = docs.filter((d) => String(d.document_type) === SALE_DOC_TYPES.proceeds);
  if (proceeds.some((d) => String(d.ai_processing_status || '').toLowerCase() === 'complete' && /^(reviewed|accepted|approved|verified)$/i.test(String(d.admin_status || '')))) {
    return { provided: true, state: 'proceeds_verified', text: 'The money from your sale is in your account' };
  }
  if (proceeds.length) return { provided: true, state: 'proceeds_received', text: 'Statement received. You can make an offer once it has been checked.' };
  if (has(SALE_DOC_TYPES.finalSettlement) || has(SALE_DOC_TYPES.netSheet)) {
    return { provided: true, state: 'needs_proceeds', text: SALE_PROCEEDS_LINE };
  }
  const reqs = (giftSale && Array.isArray(giftSale.netSheetRequests)) ? giftSale.netSheetRequests : [];
  const addr = String((s && s.property_address) || '').trim().toLowerCase();
  const req = reqs.find((r) => Number(r.id) === Number(s && s.net_sheet_request_id))
    || (addr ? reqs.find((r) => String(r.propertyAddress || '').trim().toLowerCase() === addr) : null);
  // 🚨 Waiting is not complete (Jesse, 2026-10-26): the net sheet is not in until the escrow or title agent sends it.
  if (req) return { provided: false, pending: true, state: 'waiting_escrow', text: `Waiting on ${req.escrowAgentName || 'your escrow agent'} for the net sheet` };
  // The classic page filed a sale's papers under its own types.
  if (['purchase_agreement', 'psa', 'net_sheet', 'settlement_statement'].some(has) || has(`asset_${s && s.id}`)) {
    return { provided: true, state: 'documents', text: 'Sale documents received' };
  }
  return { provided: false, state: 'needs_net_sheet', text: 'Add the net sheet, or ask your escrow or title agent for it' };
}

// ── 🔑 ONE LABEL FOR EVERY ASSETS ROW (Jesse, 2026-10-26 — step-5 Assets tidy-up, item 1) ──────────────────────────
// "<Type> · <Bank> ••<last4>", plus the borrower's nickname when they gave one. A gift is "Gift · <donor>", a home sale
// "Home sale · <address>". An entry from before the separate fields keeps the one name it was typed with.
const ASSET_TYPE_LABEL = { personal: 'Checking or savings', checking: 'Checking', savings: 'Savings', retirement: 'Retirement',
  investment: 'Investment', stocks: 'Investment', crypto: 'Crypto', other: 'Other' };
export function assetSourceLabel(s) {
  if (!s) return 'Account';
  if (isGiftSource(s)) return `Gift · ${donorOf(s) || 'your donor'}`;
  if (isSaleSource(s)) return `Home sale · ${String(s.property_address || String(s.source_name || '').replace(/^Sale of\s+/i, '')).trim() || 'your home'}`;
  const type = ASSET_TYPE_LABEL[String(s.source_type || '').toLowerCase()] || 'Account';
  const bank = String(s.institution || '').trim();
  const last4 = String(s.account_last4 || '').replace(/\D/g, '');
  // 🚨 NEVER ENGINE WORDING ON A BORROWER SCREEN (np-step5, the mockup pack v2 review): a staff-applied figure's row is
  // named "AI UW — Checking / Savings" in the back office (utils/staffAppliedAssets); the borrower reads its category only.
  const staffName = (v) => String(v || '').replace(/^\s*AI UW\s*[—–-]\s*/i, '').trim();
  const where = bank ? `${staffName(bank)}${last4.length === 4 ? ` ••${last4}` : ''}` : staffName(s.source_name);
  const nick = String(s.nickname || '').trim();
  return `${type}${where ? ` · ${where}` : ''}${nick ? ` (${nick})` : ''}`;
}

/** A gift or sale row's status, or null for an ordinary account. */
export function specialSourceStatus(s, ctx) {
  if (isGiftSource(s)) return giftRowStatus(s, ctx);
  if (isSaleSource(s)) return saleRowStatus(s, ctx);
  return null;
}

/**
 * The borrower-facing assets figure: documented dollars against the down
 * payment, which is the question the section is actually asking.
 *
 * ⚠️ A TYPED FIGURE IS NOT AN ASSET UNTIL SOMETHING BACKS IT. One borrower was
 * approved on declared sources alone and the shortfall surfaced at the offer
 * stage. A source carrying `documented` came from a statement somebody read;
 * every other source needs its own upload.
 *
 * ── 🚨 PHASE 4 ITEM 2: THE LEGACY EXEMPTION IS GONE ─────────────────────────
 *
 * Jesse: "Drop requires_document=false for undocumented asset sources."
 *
 * 🚨 WHAT IT USED TO DO. A source predating the requires_document column —
 * `requiresDoc` falsy — counted towards the down payment if there was ANY
 * asset-typed document anywhere on the file. One bank statement for one account
 * made every legacy source count, whatever account it named. Measured against
 * production: 12 real borrowers have sources, 24 of their sources are legacy,
 * and NOT ONE of them has its own `asset_<id>` upload. So the exemption was not
 * covering an edge case; it was the only reason those dollars counted at all.
 *
 * 🔑 AND THE SHORTFALL IT HIDES SURFACES AS SOMEBODY'S EARNEST MONEY. 100% on
 * money nobody has seen is the reading that sent a borrower to the offer stage
 * short of their down payment. Two real borrowers drop (#45 100%→96%, #69
 * 100%→50%), both `not_moving_forward`; no active file moves, because the one
 * submitted borrower with sources (#373) has hers from the underwriting table
 * carrying `documented: true`.
 *
 * ⚠️ `documented` IS NOT THE SAME EXEMPTION AND STAYS. Those rows exist BECAUSE
 * underwriting read a statement — utils/borrowerAssetSources.js sets the flag
 * when it synthesises them from asset_sources — so demanding a borrower upload
 * for them would ask for a document Doorly already has.
 */
export function assetsProvidedPct(o, views) {
  return assetsCompletion(o, views).pct;
}

/**
 * 🔑 ONE RULE FOR THE BAR AND THE STATUS LINE (Jesse, 2026-10-26): the section's percentage and the list of what is
 * still to finish come from this one function, so step 5 can never show 100% beside a source still waiting.
 * @returns {{ pct:number, complete:boolean, incomplete: Array<{ id, label, state, pending:boolean, text:string }> }}
 */
// 🔑 A STATEMENT READ WITH NO BALANCE (Jesse, 2026-10-26 — backend utils/assetDocumentation balanceUnread): the account
// is not complete, never ✓, and the section is not 100% until a clearer copy is read.
export const NO_BALANCE_LINE = 'We couldn\u2019t read a balance from this statement. Upload a clearer copy.';
export function assetsCompletion(o, views) {
  if (o.plaid && o.plaid.assets) return { pct: 100, complete: true, incomplete: [] };
  const sources = Array.isArray(o.assetSources) ? o.assetSources : [];
  const docs = Array.isArray(o.documents) ? o.documents : [];
  const assetDocsPresent = docs.some((d) => ASSET_DOC_TYPES.some((t) => docMatches(d, t)));
  // 🔑 COMPLETE BY PAPERWORK, NOT BY A DOLLAR TARGET (Jesse, 2026-10-25 — the assets write-up): Assets is done when every
  // account or source the borrower declared has what AFM v2 asks for it — its statement, a gift's signed letter and
  // donor or deposit statement, a sale's net sheet or settlement (whether a statement is current and in their name is
  // the check's, raised as a request card). Asking a donor or an escrow agent is pending, not done (2026-10-26). The gap
  // to the planned down payment is information (constants/assetStatusLine), and pricing uses only what is verified. A
  // source they cannot document is removed, not ignored.
  if (sources.length > 0) {
    const incomplete = [];
    for (const s of sources) {
      if (s.documented) continue;
      if (s.balanceUnread) {
        incomplete.push({ id: s.id, label: assetSourceLabel(s), state: 'no_balance', pending: false, text: NO_BALANCE_LINE });
        continue;
      }
      const special = specialSourceStatus(s, { giftSale: o.giftSale, documents: docs });
      if (special ? special.provided : docs.some((d) => docMatches(d, `asset_${s.id}`))) continue;
      incomplete.push({ id: s.id, label: assetSourceLabel(s), state: special ? special.state : 'needs_statement',
        pending: !!(special && special.pending), text: special ? special.text : 'Add this account\u2019s statement' });
    }
    const done = sources.length - incomplete.length;
    // Declared but nothing documented yet reads as started (50), as it did; partly documented is the share done.
    const pct = done === sources.length ? 100 : done === 0 ? 50 : Math.min(99, Math.max(50, Math.round((done / sources.length) * 100)));
    return { pct, complete: pct === 100, incomplete };
  }
  // 🔑 JOINT PART 2 B (Jesse, 2026-10-27 — decision 3: the gate counts the FILE's verified total). An applicant with no
  // accounts of their own, on a file where money in the other applicant's name is verified, is done: the down payment
  // can sit entirely in one name. `o.jointAssets` comes from the server (backend utils/documentedAssets jointAssetsFor)
  // and is null on a solo file, so a solo borrower's rule is unchanged.
  if (o.jointAssets && o.jointAssets.othersVerified) return { pct: 100, complete: true, incomplete: [], viaFile: true };
  // A gift letter on file with no source declared is still something supplied.
  if (views && views.some((v) => v.id === 'gift_letter' && v.uploadedCount > 0)) return { pct: 50, complete: false, incomplete: [] };
  if (assetDocsPresent) return { pct: 50, complete: false, incomplete: [] };
  return { pct: 0, complete: false, incomplete: [] };
}
